import type { Card } from "@/context/AppContext";
import type { DeckSourceProviderId } from "@/types/deckSource";

export type DeckFormat =
  | "Casual"
  | "Standard"
  | "Expanded"
  | "GLC"
  | "Pocket"
  | "No Specific Format";

export type DeckIntent =
  | "casual-fun"
  | "beginner-friendly"
  | "pokemon-specific"
  | "type-themed"
  | "budget-playable"
  | "deck-core"
  | "upgrade-kit"
  | "competitive-inspired";

export type DeckBuildMode =
  | "build-from-inventory"
  | "import-deck-list"
  | "use-local-template"
  | "build-around-pokemon"
  | "build-around-type"
  | "create-deck-core"
  | "create-upgrade-kit";

export type DeckSection = "Pokemon" | "Trainer" | "Energy" | "Unknown";

/** Parsed line from a TCGplayer-style deck list. */
export interface ParsedDeckLine {
  quantity: number;
  name: string;
  setCode?: string;
  cardNumber?: string;
  section?: DeckSection;
  raw: string;
}

/** Result of matching a single parsed deck line against the seller's inventory. */
export interface DeckInventoryMatch {
  requested: ParsedDeckLine;
  matchedCards: Card[];
  selectedCardIds: string[];
  ownedQty: number;
  requestedQty: number;
  missingQty: number;
  substitutes: Card[];
  /** 0–1; 1 = exact name+setCode+cardNumber match. */
  matchConfidence: number;
  matchReason: string;
}

export interface ProductDeckLine {
  cardId?: string;
  name: string;
  requestedQty: number;
  includedQty: number;
  role: DeckSection;
  required: boolean;
  sourceLine?: string;
}

export interface MissingDeckCard {
  name: string;
  missingQty: number;
  role: DeckSection;
  priority: "Required" | "Recommended" | "Optional";
}

export interface DeckSubstitution {
  requestedName: string;
  substituteCardId: string;
  substituteName: string;
  reason: string;
}

export interface DeckScore {
  /** 0–100. */
  completenessScore: number;
  /** 0–100. */
  playabilityScore: number;
  /** 0–100. */
  themeScore: number;
  /** 0–100. */
  inventoryFitScore: number;
  /** 0–100. */
  valueScore: number;
  /** Weighted blend; see features/deckScoring.ts. */
  finalScore: number;
  warnings: string[];
}

/** Persisted on Product when the product is a deck-shaped product. */
export interface ProductDeckBuild {
  format: DeckFormat;
  intent: DeckIntent;
  archetypeName?: string;
  heroPokemon?: string;
  typeTheme?: string;
  sourceDeckUrl?: string;
  sourceDeckName?: string;
  sourceProvider?: DeckSourceProviderId;
  isCompleteDeck: boolean;
  legalityVerified: boolean;
  deckList: ProductDeckLine[];
  missingCards: MissingDeckCard[];
  substitutesUsed: DeckSubstitution[];
  deckScore: DeckScore;
}

export interface DeckProfile {
  targetCards: number;
  pokemonMin: number;
  pokemonMax: number;
  trainerMin: number;
  trainerMax: number;
  energyMin: number;
  energyMax: number;
  maxCopiesPerCard: number;
}

export const CASUAL_BEGINNER_DECK_PROFILE: DeckProfile = {
  targetCards: 60,
  pokemonMin: 16,
  pokemonMax: 24,
  trainerMin: 20,
  trainerMax: 32,
  energyMin: 10,
  energyMax: 18,
  maxCopiesPerCard: 4,
};

export const KID_FRIENDLY_DECK_PROFILE: DeckProfile = {
  targetCards: 60,
  pokemonMin: 20,
  pokemonMax: 28,
  trainerMin: 14,
  trainerMax: 24,
  energyMin: 14,
  energyMax: 20,
  maxCopiesPerCard: 4,
};

export const DECK_CORE_PROFILE: DeckProfile = {
  targetCards: 20,
  pokemonMin: 8,
  pokemonMax: 16,
  trainerMin: 4,
  trainerMax: 12,
  energyMin: 0,
  energyMax: 4,
  maxCopiesPerCard: 4,
};
